import React, { useState, useMemo } from "react";
import { Table, Button, Row, Col, Pagination, Form } from "react-bootstrap";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  ColumnDef,
  SortingState,
  PaginationState,
} from "@tanstack/react-table";
import { NetworkUser } from "./hooks/use-network";
import { WEB_BASE_URL } from "../../config";
import { getRankImage } from "../../utils/getRankImage";
import { getRankColor } from "../../utils/getRankColor";
import { hasRank } from "../../utils/hasRank";

interface TeamTableProps {
  teamMembers: NetworkUser[];
  onViewNetwork: (memberId: string) => void;
  isLoading?: boolean;
  currentUserId: string | undefined;
}

const TeamTable: React.FC<TeamTableProps> = ({
  teamMembers,
  onViewNetwork,
  isLoading = false,
  currentUserId,
}) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // Function to copy code to clipboard
  const copyToClipboard = (code: string) => {
    const referralLink = `${WEB_BASE_URL}/sign-up?code=${code}`;
    navigator.clipboard.writeText(referralLink);
    alert("¡Enlace copiado al portapapeles!");
  };

  // Column helper for type safety
  const columnHelper = createColumnHelper<NetworkUser>();

  // Define columns
  const columns = useMemo<ColumnDef<NetworkUser>[]>(
    () => [
      columnHelper.accessor("multyhealth_code", {
        header: "Código",
        cell: (info) => (
          <div className="d-flex align-items-center">
            <span className="me-2">{info.getValue()}</span>
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={() => copyToClipboard(info.getValue())}
            >
              <i className="ri-file-copy-line"></i>
            </Button>
          </div>
        ),
      }) as ColumnDef<NetworkUser>,

      columnHelper.accessor("name", {
        header: "Nombre",
        cell: (info) => {
          const user = info.row.original;

          return (
            <div className="d-flex align-items-center gap-2">
              <span>{user.name}</span>

              {user.networkLevel === "Nivel 1" && user.cellphone && (
                <a
                  href={`https://wa.me/${user.cellphone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-success"
                  title="Escribir por WhatsApp"
                >
                  <i
                    className="ri-whatsapp-fill"
                    style={{
                      fontSize: 20,
                      color: "#25D366",
                    }}
                  />
                </a>
              )}
            </div>
          );
        },
      }) as ColumnDef<NetworkUser>,

      columnHelper.accessor("networkLevel", {
        header: "Nivel",
        cell: (info) => <span className="text-white">{info.getValue()}</span>,
      }) as ColumnDef<NetworkUser>,

      columnHelper.accessor("network.loyalty_status", {
        header: "Estado",
        cell: (info) => {
          const active = info.getValue();

          return (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 72,
                height: 32,
                padding: "0 12px",
                borderRadius: 6,

                background: "#111418",

                border: `1px solid ${active ? "#2dd36f" : "#ff3b30"}`,

                color: active ? "#2dd36f" : "#ff3b30",

                boxShadow: active
                  ? `
                inset 0 0 10px rgba(45,211,111,.15),
                0 0 8px rgba(45,211,111,.20)
              `
                  : `
                inset 0 0 10px rgba(255,59,48,.15),
                0 0 8px rgba(255,59,48,.20)
              `,

                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {active ? "Activo" : "Inactivo"}
            </div>
          );
        },
      }) as ColumnDef<NetworkUser>,

      ...(!currentUserId
        ? [
            columnHelper.accessor("monthPoints", {
              header: "Punto Mes",
              // Los puntos del mes sólo cuentan si la persona está activa:
              // mostrárselos a un inactivo da a entender que le suman, y no.
              cell: (info) => {
                const active = info.row.original.network?.loyalty_status;

                if (!active) {
                  return <span className="text-white-50">—</span>;
                }

                return (
                  <span className="text-white">
                    {info.getValue()?.toLocaleString()}
                  </span>
                );
              },
            }) as ColumnDef<NetworkUser>,
          ]
        : []),

      columnHelper.accessor("last_rank.rank.name", {
        header: "Último Rango",
        cell: (info) => {
          const user = info.row.original;

          if (!hasRank(user.last_rank?.rank?.name)) {
            return "Sin rango";
          }

          return (
            <div className="d-flex align-items-center gap-2">
              <img
                src={getRankImage(user.last_rank?.rank?.name)}
                alt={user.last_rank?.rank?.name}
                style={{
                  width: 20,
                  height: 20,
                  objectFit: "contain",
                  filter: `drop-shadow(0 0 5px ${getRankColor(user.last_rank?.rank?.name)})`,
                }}
              />

              <span>{user.last_rank?.rank?.name}</span>
            </div>
          );
        },
      }) as ColumnDef<NetworkUser>,

      columnHelper.accessor("referenced_by_count", {
        header: "Usuarios",
        cell: (info) => info.getValue() || 0,
      }) as ColumnDef<NetworkUser>,

      columnHelper.display({
        id: "actions",
        header: "Acciones",
        cell: (info) => (
          <Button
            variant="outline-success"
            size="sm"
            onClick={() => onViewNetwork(info.row.original.id)}
          >
            <i className="ri-user-follow-fill"></i>
          </Button>
        ),
      }),
    ],
    [onViewNetwork, columnHelper, currentUserId],
  );

  // Global filter function for searching by code and name
  const globalFilterFn = (
    row: any,
    columnId: string,
    value: string,
  ): boolean => {
    const searchValue = value.toLowerCase();
    const code = row.original.multyhealth_code?.toLowerCase() || "";
    const name = row.original.name?.toLowerCase() || "";
    return code.includes(searchValue) || name.includes(searchValue);
  };

  // Create table instance
  const table = useReactTable({
    data: teamMembers || [],
    columns,
    state: {
      sorting,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn,
    debugTable: false,
  });

  if (isLoading) {
    return (
      <div className="table-responsive mt-3">
        <div className="text-center p-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Cargando usuarios...</span>
          </div>
          <p className="mt-2 mb-0">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3">
      {/* Search and Controls */}
      <Row className="mb-3">
        <Col md={6} className="d-flex align-items-end">
          <Form.Group>
            <Form.Label>Mostrar</Form.Label>
            <Form.Select
              style={{ height: "50px" }}
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
            >
              {[5, 10, 20, 30, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize} registros
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Buscar por código o nombre</Form.Label>
            <Form.Control
              type="text"
              placeholder="Buscar usuario..."
              value={globalFilter ?? ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive">
        <Table className="align-middle">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={
                      header.column.getCanSort()
                        ? "cursor-pointer user-select-none"
                        : ""
                    }
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="d-flex align-items-center">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                      {header.column.getCanSort() && (
                        <span className="ms-1">
                          {{
                            asc: <i className="ri-arrow-up-s-line"></i>,
                            desc: <i className="ri-arrow-down-s-line"></i>,
                          }[header.column.getIsSorted() as string] ?? (
                            <i className="ri-expand-up-down-line text-muted"></i>
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  {globalFilter
                    ? "No se encontraron usuarios que coincidan con la búsqueda."
                    : "No hay usuarios en la red de este usuario."}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {table.getPageCount() > 1 && (
        <Row className="mt-3">
          <Col md={6} className="d-flex align-items-center">
            <span className="text-muted">
              Mostrando{" "}
              {table.getState().pagination.pageIndex *
                table.getState().pagination.pageSize +
                1}{" "}
              a{" "}
              {Math.min(
                (table.getState().pagination.pageIndex + 1) *
                  table.getState().pagination.pageSize,
                table.getFilteredRowModel().rows.length,
              )}{" "}
              de {table.getFilteredRowModel().rows.length} registros
            </span>
          </Col>
          <Col md={6} className="d-flex justify-content-end">
            <Pagination className="mb-0">
              <Pagination.First
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              />
              <Pagination.Prev
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              />

              {/* Page numbers */}
              {Array.from(
                { length: Math.min(5, table.getPageCount()) },
                (_, i) => {
                  const pageIndex = table.getState().pagination.pageIndex;
                  const totalPages = table.getPageCount();
                  let startPage = Math.max(0, pageIndex - 2);
                  let endPage = Math.min(totalPages - 1, startPage + 4);

                  if (endPage - startPage < 4) {
                    startPage = Math.max(0, endPage - 4);
                  }

                  const currentPage = startPage + i;
                  if (currentPage <= endPage) {
                    return (
                      <Pagination.Item
                        key={currentPage}
                        active={currentPage === pageIndex}
                        onClick={() => table.setPageIndex(currentPage)}
                      >
                        {currentPage + 1}
                      </Pagination.Item>
                    );
                  }
                  return null;
                },
              )}

              <Pagination.Next
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              />
              <Pagination.Last
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              />
            </Pagination>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default TeamTable;
